import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import config from '../../config';
import axios from 'axios';

// MUI Components
import {
  Container,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Box,
  Divider,
  MenuItem,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  CircularProgress,
} from '@mui/material';

import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

export default function AddAndManageItems() {
  const company = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');
  const token = sessionStorage.getItem('token');
  const Companycurrency = company?.currency ?? '';

  const navigate = useNavigate();
  // ==================== ADD FORM STATES ====================
  const [categories, setCategories] = useState([]);
  const [departments, setDepartments] = useState([]);

  const [categoryId, setCategoryId] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [itemName, setItemName] = useState('');
  const [hsnCode, setHsnCode] = useState('');

  const [gst, setGst] = useState('');
  const [excludePrice, setExcludePrice] = useState('');
  const [includePrice, setIncludePrice] = useState('');

  const [isVatApplicable, setIsVatApplicable] = useState(false);
  const [vatRate, setVatRate] = useState('');
  // #2: item image — center-cropped square, kept as a base64 data URL so it
  // stores in ONE DB column (no file server needed). Rendered at 70px in the
  // admin thumb but up to ~200px in the QR/smart menu, so we keep a 400x400
  // source (never upscaled past the original) → crisp everywhere, still small.
  const [itemImage, setItemImage] = useState('');
  const IMG_SIZE = 400;
  // #3: normalise Excel Food Type into Veg|NonVeg|Egg|Jain|Vegan.
  const normFoodType = (x) => {
    const v = String(x || '').trim().toLowerCase().replace(/[^a-z]/g, '');
    if (v === 'nonveg' || v === 'non') return 'NonVeg';
    if (v === 'egg') return 'Egg';
    if (v === 'jain') return 'Jain';
    if (v === 'vegan') return 'Vegan';
    return 'Veg';
  };
  const cropTo70 = (file) =>
    new Promise((resolve) => {
      if (!file) return resolve('');
      const reader = new FileReader();
      reader.onload = () => {
        const img = new window.Image();
        img.onload = () => {
          const side = Math.min(img.width, img.height); // largest centered square
          const sx = (img.width - side) / 2;
          const sy = (img.height - side) / 2;
          // Don't upscale a tiny source above its own resolution.
          const out = Math.min(IMG_SIZE, side);
          const canvas = document.createElement('canvas');
          canvas.width = out;
          canvas.height = out;
          const ctx = canvas.getContext('2d');
          ctx.imageSmoothingQuality = 'high';
          ctx.drawImage(img, sx, sy, side, side, 0, 0, out, out);
          resolve(canvas.toDataURL('image/jpeg', 0.92));
        };
        img.onerror = () => resolve('');
        img.src = reader.result;
      };
      reader.onerror = () => resolve('');
      reader.readAsDataURL(file);
    });
  // Sellable-in-POS flag. Defaults true so a brand-new item behaves like
  // an existing menu item (shows on the POS Create Order screen). Admin
  // unchecks for raw materials / ingredients used only in recipes.
  const [isSellable, setIsSellable] = useState(true);
  // Veg / Non-Veg classification for the digital (QR) menu.
  const [foodType, setFoodType] = useState('Veg');
  // #2: optional multi-line item description (shown on enabled surfaces).
  const [description, setDescription] = useState('');
  // #2: which surfaces show the description (company-level display settings).
  const [descDisplay, setDescDisplay] = useState({
    pos: true,
    qrMenu: true,
    customerMenu: true,
    onlineOrdering: true,
    printedBill: false,
    invoice: false,
    kitchenKot: false,
    waiterApp: false,
  });
  const [savingDesc, setSavingDesc] = useState(false);

  // ==================== TABLE STATES ====================
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchItem, setSearchItem] = useState('');
  const [searchCategory, setSearchCategory] = useState('');

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [sortField, setSortField] = useState('name');
  const [sortDir, setSortDir] = useState('asc');

  const [editOpen, setEditOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [savingItem, setSavingItem] = useState(false);

  // ==================== EXCEL IMPORT STATES ====================
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState({ done: 0, total: 0 });
  const [importReport, setImportReport] = useState({
    success: [],
    skipped: [],
    failed: [],
  });

  // ==================== FETCH CATEGORIES & DEPARTMENTS ====================
  useEffect(() => {
    if (!company?.id) return;

    const fetchData = async () => {
      try {
        const [catRes, deptRes] = await Promise.all([
          axios.get(
            `${config.API_BASE_URL}/Home/GetAllCategory/${company.id}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            },
          ),
          axios.get(
            `${config.API_BASE_URL}/Home/GetDepartments/${company.id}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            },
          ),
        ]);

        setCategories(catRes.data || []);
        const deptData = deptRes.data || [];
        setDepartments(deptData);

        // Store the department's unique id (not the printer name). Multiple
        // departments can share the same printer name, which makes MUI Select
        // highlight every matching MenuItem and breaks selection. The printer
        // name is derived on save by looking up the chosen id.
        if (deptData.length > 0) {
          setSelectedDepartment(deptData[0].id ?? deptData[0].Id ?? '');
        }
      } catch (err) {
        console.error('Error loading data', err);
      }
    };

    fetchData();
  }, [company?.id, token]);

  // ==================== FETCH ITEMS ====================
  const fetchItems = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${config.API_BASE_URL}/home/Getallrestitem/${company?.id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setItems(res.data || []);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchItems();
  }, []);

  // #2: load the company's description-display settings.
  useEffect(() => {
    if (!company?.id) return;
    fetch(`${config.API_BASE_URL}/qrmenu/desc-display/${company.id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((d) => {
        if (d?.json) {
          try {
            setDescDisplay((prev) => ({ ...prev, ...JSON.parse(d.json) }));
          } catch {
            /* keep defaults */
          }
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveDescDisplay = async () => {
    setSavingDesc(true);
    try {
      const res = await fetch(`${config.API_BASE_URL}/qrmenu/desc-display`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          CompanyId: company?.id,
          Json: JSON.stringify(descDisplay),
        }),
      });
      if (!res.ok) throw new Error();
      toast.success('Description display settings saved');
    } catch {
      toast.error('Failed to save display settings');
    } finally {
      setSavingDesc(false);
    }
  };

  // ==================== PRICE HANDLERS ====================
  const handleExcludeChange = (value) => {
    setExcludePrice(value);

    const rate = isVatApplicable ? vatRate : gst;

    if (value && rate) {
      const include =
        parseFloat(value) + (parseFloat(value) * parseFloat(rate)) / 100;
      setIncludePrice(include.toFixed(2));
    } else {
      setIncludePrice('');
    }
  };

  // ✅ FIX 1: Include price field now uses the correct handler
  const handleIncludeChange = (value) => {
    setIncludePrice(value);

    const rate = isVatApplicable ? vatRate : gst;

    if (value && rate) {
      const exclude = parseFloat(value) / (1 + parseFloat(rate) / 100);
      setExcludePrice(exclude.toFixed(2));
    } else {
      setExcludePrice('');
    }
  };

  // ✅ FIX 2: GST field is now called with the 'gst' type
  const handleTaxChange = (value, type) => {
    if (type === 'gst') {
      setGst(value);
    } else {
      setVatRate(value);
    }

    if (excludePrice && value) {
      const include =
        parseFloat(excludePrice) +
        (parseFloat(excludePrice) * parseFloat(value)) / 100;
      setIncludePrice(include.toFixed(2));
    }
  };

  const handleVatToggle = (checked) => {
    setIsVatApplicable(checked);
    if (checked) setGst('');
    else setVatRate('');
  };

  // ==================== SAVE ITEM ====================
  const saveItem = async () => {
    if (!categoryId || !itemName || !selectedDepartment) {
      toast.error('Category, Item Name and Department are required');
      return;
    }
    if (!isVatApplicable && (!includePrice || !gst)) {
      toast.error('Price (Include GST) and GST are required for normal items');
      return;
    }
    if (isVatApplicable && !vatRate) {
      toast.error('VAT Rate is required');
      return;
    }

    // selectedDepartment holds the dept.id — resolve the actual DEPARTMENT
    // NAME and send that (not the printer name). Several departments can share
    // one printer, so sending the printer name lost which department was
    // chosen; the department name keeps the item → department → printer
    // mapping exact.
    const selectedDept = departments.find(
      (d) => (d.id ?? d.Id) === selectedDepartment,
    );
    const departmentName =
      selectedDept?.department ||
      selectedDept?.Department ||
      selectedDept?.Printername ||
      selectedDept?.printername ||
      '';

    const payload = {
      categoryId,
      itemName,
      hsnCode,
      itemPrice: includePrice ? parseFloat(includePrice) : 0,
      gst: isVatApplicable ? 0 : gst ? parseFloat(gst) : 0,
      vatRate: isVatApplicable ? parseFloat(vatRate) : 0,
      isVatApplicable,
      isSellable,
      foodType, // Veg | NonVeg | Egg | Jain — for the QR digital menu
      description, // #2: multi-line description
      department: departmentName,
      companyid: company?.id,
      image: itemImage, // #2: base64 70x70 (optional)
    };

    try {
      setSavingItem(true);
      await axios.post(`${config.API_BASE_URL}/Home/CreateItem`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      toast.success('Item created successfully!');
      fetchItems();

      // Reset Form
      setCategoryId('');
      setItemName('');
      setItemImage('');
      setHsnCode('');
      setExcludePrice('');
      setIncludePrice('');
      setGst('');
      setVatRate('');
      setIsVatApplicable(false);
      setFoodType('Veg');
      setDescription('');
      setSavingItem(false);
    } catch (err) {
      console.error(err);
      toast.error('Failed to create item');
      setSavingItem(false);
    }
  };

  // ==================== TABLE LOGIC ====================
  const filtered = items.filter(
    (x) =>
      x.name?.toLowerCase().includes(searchItem.toLowerCase()) &&
      x.categoryName?.toLowerCase().includes(searchCategory.toLowerCase()),
  );

  const sorted = [...filtered].sort((a, b) => {
    if (sortField === 'price') {
      return sortDir === 'asc' ? a.price - b.price : b.price - a.price;
    }
    const valA = (a[sortField] || '').toLowerCase();
    const valB = (b[sortField] || '').toLowerCase();
    return sortDir === 'asc'
      ? valA.localeCompare(valB)
      : valB.localeCompare(valA);
  });

  const paged = sorted.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const exportToExcel = () => {
    const formatted = filtered.map((i, index) => ({
      '#': index + 1,
      Item: i.name,
      Category: i.categoryName,
      Price: i.price,
      Status: i.isActive ? 'Active' : 'Inactive',
    }));

    const ws = XLSX.utils.json_to_sheet(formatted);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Items');
    XLSX.writeFile(wb, 'ItemReport.xlsx');
  };

  // ==================== EXCEL IMPORT ====================
  // Template columns:
  //   CategoryName  (required — auto-created if not exists)
  //   ItemName      (required)
  //   Price         (required — treated as include-GST price, same as the
  //                  form's `includePrice` field)
  //   GST           (e.g. 5, 12, 18 — used when IsVatApplicable is false)
  //   HSNCode       (optional)
  //   Department    (optional — defaults to first available printer)
  //   IsVatApplicable (true/false — default false)
  //   VatRate       (used only when IsVatApplicable=true)
  const downloadItemTemplate = () => {
    const sample = [
      {
        CategoryName: 'Beverages',
        ItemName: 'Masala Chai',
        Price: 50,
        GST: 5,
        HSNCode: '0902',
        Department: departments[0]?.printername || departments[0]?.Printername || 'Kitchen',
        IsVatApplicable: false,
        VatRate: '',
        Description: 'Optional item description',
        FoodType: 'Veg',
      },
      {
        CategoryName: 'Starters',
        ItemName: 'Paneer Tikka',
        Price: 220,
        GST: 5,
        HSNCode: '2106',
        Department: departments[0]?.printername || departments[0]?.Printername || 'Kitchen',
        IsVatApplicable: false,
        VatRate: '',
        Description: 'Optional item description',
        FoodType: 'Veg',
      },
    ];
    const ws = XLSX.utils.json_to_sheet(sample);
    ws['!cols'] = [
      { wch: 18 },
      { wch: 24 },
      { wch: 10 },
      { wch: 8 },
      { wch: 12 },
      { wch: 14 },
      { wch: 14 },
      { wch: 10 },
      { wch: 24 },
      { wch: 10 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Items');
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(
      new Blob([buf], { type: 'application/octet-stream' }),
      'Item_Import_Template.xlsx',
    );
  };

  // Reads an Excel/CSV file, validates each row, auto-creates missing
  // categories via /Home/CreateCategory, then POSTs each item to /Home/CreateItem.
  // Reports added/skipped/failed at the end so the user knows exactly what
  // succeeded.
  const handleImportExcel = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (ev) => {
      const _t0 = Date.now(); // #1: import duration
      try {
        const wb = XLSX.read(ev.target.result, { type: 'array' });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(sheet, { defval: '' });

        const cleaned = rows
          .map((r, idx) => ({
            row: idx + 2, // +2 because header is row 1, data starts row 2
            categoryName: String(
              r.CategoryName ?? r['Category Name'] ?? r.Category ?? '',
            ).trim(),
            itemName: String(
              r.ItemName ?? r['Item Name'] ?? r.Item ?? r.Name ?? '',
            ).trim(),
            price: Number(
              r.Price ?? r['Price (Inc GST)'] ?? r.IncludePrice ?? 0,
            ),
            gst: Number(r.GST ?? r.Gst ?? r.gst ?? 0),
            hsnCode: String(
              r.HSNCode ?? r['HSN Code'] ?? r.HsnCode ?? '',
            ).trim(),
            department: String(
              r.Department ?? r.Dept ?? r.Printer ?? '',
            ).trim(),
            isVatApplicable: ['true', '1', 'yes', 'y'].includes(
              String(
                r.IsVatApplicable ?? r['Is VAT Applicable'] ?? 'false',
              )
                .trim()
                .toLowerCase(),
            ),
            vatRate: Number(r.VatRate ?? r['VAT Rate'] ?? 0),
            description: String(r.Description ?? r.Desc ?? '').trim(),
            foodType: normFoodType(r.FoodType ?? r['Food Type'] ?? r.Foodtype ?? ''),
          }))
          .filter((r) => r.categoryName && r.itemName);

        if (cleaned.length === 0) {
          toast.error(
            'No valid rows found. Required columns: CategoryName, ItemName, Price.',
          );
          return;
        }

        // Build category name → id map. Auto-create any missing categories so
        // the user doesn't have to pre-create them.
        const catMap = new Map();
        categories.forEach((c) => {
          catMap.set(
            (c.category_name || c.categoryName || '').trim().toLowerCase(),
            c.id ?? c.ID,
          );
        });

        setImporting(true);
        setImportProgress({ done: 0, total: cleaned.length });
        const report = { success: [], skipped: [], failed: [] };

        const defaultDept =
          departments[0]?.printername ||
          departments[0]?.Printername ||
          'Kitchen';

        // Track existing item names per category to short-circuit duplicates
        // (case-insensitive). We don't have a per-category items list cached,
        // so use the global items list.
        const existingItemKey = new Set(
          items.map(
            (it) =>
              `${(it.categoryName || '').toLowerCase()}|${(it.name || '').toLowerCase()}`,
          ),
        );

        for (let i = 0; i < cleaned.length; i++) {
          const row = cleaned[i];
          try {
            // Step 1 — Resolve / create category
            const catKey = row.categoryName.toLowerCase();
            let cateId = catMap.get(catKey);
            if (!cateId) {
              const fd = new FormData();
              fd.append('CategoryName', row.categoryName);
              fd.append('CompanyId', company?.id);
              const catRes = await axios.post(
                `${config.API_BASE_URL}/Home/CreateCategory`,
                fd,
                {
                  headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data',
                  },
                },
              );
              cateId =
                catRes?.data?.id ??
                catRes?.data?.ID ??
                catRes?.data?.data?.id ??
                null;
              // Refresh categories from server if we couldn't extract an id —
              // every CreateCategory call returns differently across handlers.
              if (!cateId) {
                const refresh = await axios.get(
                  `${config.API_BASE_URL}/home/GetCategory/${company.id}`,
                  { headers: { Authorization: `Bearer ${token}` } },
                );
                const list = Array.isArray(refresh.data)
                  ? refresh.data
                  : refresh.data?.data || [];
                list.forEach((c) => {
                  catMap.set(
                    (c.category_name || c.categoryName || '')
                      .trim()
                      .toLowerCase(),
                    c.id ?? c.ID,
                  );
                });
                cateId = catMap.get(catKey);
              }
              if (cateId) catMap.set(catKey, cateId);
            }

            if (!cateId) {
              report.failed.push({
                row: row.row,
                name: row.itemName,
                reason: 'Could not create/find category',
              });
              setImportProgress({ done: i + 1, total: cleaned.length });
              continue;
            }

            // Step 2 — Skip duplicates
            const itemKey = `${row.categoryName.toLowerCase()}|${row.itemName.toLowerCase()}`;
            if (existingItemKey.has(itemKey)) {
              report.skipped.push({
                row: row.row,
                name: row.itemName,
                category: row.categoryName,
                reason: 'Already exists',
              });
              setImportProgress({ done: i + 1, total: cleaned.length });
              continue;
            }

            // Step 3 — Create item
            const payload = {
              categoryId: cateId,
              itemName: row.itemName,
              hsnCode: row.hsnCode || '',
              itemPrice: row.price || 0,
              gst: row.isVatApplicable ? 0 : row.gst || 0,
              vatRate: row.isVatApplicable ? row.vatRate || 0 : 0,
              isVatApplicable: row.isVatApplicable,
              department: row.department || defaultDept,
              description: row.description || '',
              foodType: row.foodType || 'Veg',
              companyid: company?.id,
            };
            await axios.post(
              `${config.API_BASE_URL}/Home/CreateItem`,
              payload,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                  'Content-Type': 'application/json',
                },
              },
            );
            report.success.push({
              row: row.row,
              name: row.itemName,
              category: row.categoryName,
            });
            existingItemKey.add(itemKey);
          } catch (err) {
            report.failed.push({
              row: row.row,
              name: row.itemName,
              reason:
                err.response?.data?.message ||
                err.message ||
                'API error',
            });
          }
          setImportProgress({ done: i + 1, total: cleaned.length });
        }

        setImportReport(report);
        fetchItems();

        // #1: log this import to Menu Upload History (best-effort).
        try {
          const cats = new Set(
            cleaned.map((c) => c.categoryName).filter(Boolean),
          );
          let base64 = '';
          try {
            const bytes = new Uint8Array(ev.target.result);
            let bin = '';
            for (let b = 0; b < bytes.length; b++)
              bin += String.fromCharCode(bytes[b]);
            base64 = btoa(bin);
          } catch {
            base64 = '';
          }
          const user = JSON.parse(sessionStorage.getItem('user') || '{}');
          const logRes = await fetch(`${config.API_BASE_URL}/menuupload/log`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              CompanyId: company?.id,
              FileName: file.name,
              FileBase64: base64,
              UploadedByUserId: user?.id || user?.Id || 0,
              UploadedByName:
                user?.username || user?.Username || user?.name || '',
              TotalCategories: cats.size,
              TotalItems: cleaned.length,
              SuccessCount: report.success.length,
              FailedCount: report.failed.length,
              FailureReason: report.failed
                .slice(0, 8)
                .map((r) => `${r.name}: ${r.reason}`)
                .join('; '),
              DurationMs: Date.now() - _t0,
            }),
          });

          // #1: link the items just imported to this menu file so it can be
          // activated/deactivated as a set. Best-effort; resolve each item's
          // category id from the name→id map built during import.
          try {
            const logJson = await logRes.json();
            const menuFileId = logJson?.id || logJson?.Id;
            if (menuFileId && report.success.length) {
              const linkItems = report.success
                .map((s) => ({
                  CateId: catMap.get((s.category || '').toLowerCase()) || 0,
                  Name: s.name,
                }))
                .filter((x) => x.CateId > 0 && x.Name);
              if (linkItems.length) {
                await fetch(`${config.API_BASE_URL}/menuupload/link`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                  },
                  body: JSON.stringify({
                    MenuFileId: menuFileId,
                    CompanyId: company?.id,
                    Items: linkItems,
                  }),
                });
              }
            }
          } catch {
            /* linking is best-effort */
          }
        } catch {
          /* history logging is best-effort */
        }
      } catch (err) {
        console.error('Excel parse failed:', err);
        toast.error(
          'Failed to read Excel file. Please use the provided template format.',
        );
      } finally {
        setImporting(false);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const toggleStatus = async (id) => {
    await axios.get(`${config.API_BASE_URL}/home/Deactiveitem/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    fetchItems();
  };

  // Flips the IsSellable flag on an existing item. Used so admin can mark
  // already-created rows (e.g. raw materials previously created for recipe
  // mapping) as non-sellable, hiding them from POS without deactivating.
  const toggleSellable = async (id, currentlySellable) => {
    await axios.get(
      `${config.API_BASE_URL}/home/ToggleItemSellable/${id}/${!currentlySellable}`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    fetchItems();
  };

  const openEdit = (item) => {
    setEditItem({ ...item });
    setEditOpen(true);
  };

  const updateItem = async () => {
    if (!editItem) return;
    await axios.get(
      `${config.API_BASE_URL}/home/Updaterestitem/${editItem.name}/${editItem.price}/${editItem.id}`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    // #2: persist the (possibly changed) image separately — the name/price
    // endpoint is path-based and can't carry a base64 image. Non-blocking.
    try {
      await axios.post(
        `${config.API_BASE_URL}/Home/UpdateItemImage/${editItem.id}`,
        JSON.stringify(editItem.image || ''),
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );
    } catch (e) {
      /* non-blocking */
    }
    // #2: persist Description + Food Type
    try {
      await axios.post(
        config.API_BASE_URL + '/Home/UpdateItemMeta/' + editItem.id,
        { Description: editItem.description || '', FoodType: editItem.foodType || 'Veg' },
        { headers: { Authorization: 'Bearer ' + token, 'Content-Type': 'application/json' } },
      );
    } catch (e) {}
    setEditOpen(false);
    fetchItems();
  };

  const categoriesList = [...new Set(items.map((x) => x.categoryName))];

  if (loading) {
    return (
      <Box display='flex' justifyContent='center' mt={10}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Container maxWidth='lg' sx={{ mt: 4, mb: 6 }}>
      {/* ── Master navigation: Department → Category → Item (in hierarchy
          order). Department first, as requested. ── */}
      <Box
        sx={{
          display: 'flex',
          gap: 1.5,
          flexWrap: 'wrap',
          mb: 3,
          justifyContent: { xs: 'stretch', sm: 'flex-start' },
        }}>
        <Button
          variant='contained'
          color='secondary'
          onClick={() => navigate('/admin/AddDepartment')}
          sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2 }}>
          + Add Department
        </Button>
        <Button
          variant='contained'
          color='info'
          onClick={() => navigate('/admin/Category')}
          sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2 }}>
          + Add Category
        </Button>
        <Button
          variant='contained'
          color='primary'
          onClick={() => navigate('/admin/AddItem')}
          sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2 }}>
          + Add Item
        </Button>
      </Box>

      {/* #2 ==================== DESCRIPTION DISPLAY SETTINGS ==================== */}
      <Card sx={{ borderRadius: 4, boxShadow: 2, mb: 3 }}>
        <CardContent>
          <Typography variant='h6' fontWeight={700} sx={{ mb: 0.5 }}>
            Description Display Settings
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
            Choose where item descriptions appear. If unchecked, the description
            never shows on that surface.
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
            {[
              ['pos', 'POS'],
              ['qrMenu', 'QR Menu'],
              ['customerMenu', 'Customer Menu'],
              ['onlineOrdering', 'Online Ordering'],
              ['printedBill', 'Printed Bill'],
              ['invoice', 'Invoice'],
              ['kitchenKot', 'Kitchen KOT'],
              ['waiterApp', 'Waiter App'],
            ].map(([k, label]) => (
              <FormControlLabel
                key={k}
                sx={{ minWidth: 180 }}
                control={
                  <Checkbox
                    checked={!!descDisplay[k]}
                    onChange={(e) =>
                      setDescDisplay((d) => ({ ...d, [k]: e.target.checked }))
                    }
                  />
                }
                label={label}
              />
            ))}
          </Box>
          <Button
            variant='contained'
            size='small'
            onClick={saveDescDisplay}
            disabled={savingDesc}
            sx={{ mt: 1, borderRadius: 2, fontWeight: 700 }}>
            {savingDesc ? 'Saving…' : 'Save Display Settings'}
          </Button>
        </CardContent>
      </Card>

      {/* ==================== ADD ITEM FORM ==================== */}
      <Card sx={{ borderRadius: 4, boxShadow: 4, mb: 6 }}>
        <CardContent>
          <Typography
            variant='h5'
            align='center'
            sx={{ mb: 3, fontWeight: 700, color: '#1976d2' }}>
            Add New Item
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Box display='flex' flexDirection='column' gap={2.5}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isVatApplicable}
                  onChange={(e) => handleVatToggle(e.target.checked)}
                />
              }
              label='VAT Applicable'
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={isSellable}
                  onChange={(e) => setIsSellable(e.target.checked)}
                />
              }
              label='Sellable in POS (uncheck for raw material / ingredient)'
            />

            {/* Veg / Non-Veg for the QR digital menu (green/red icon + filter). */}
            <FormControl fullWidth size='small'>
              <InputLabel>Food Type</InputLabel>
              <Select
                value={foodType}
                label='Food Type'
                onChange={(e) => setFoodType(e.target.value)}>
                <MenuItem value='Veg'>🟢 Veg</MenuItem>
                <MenuItem value='NonVeg'>🔴 Non-Veg</MenuItem>
                <MenuItem value='Egg'>🟠 Egg</MenuItem>
                <MenuItem value='Vegan'>🌱 Vegan</MenuItem>
                <MenuItem value='Jain'>🟡 Jain</MenuItem>
              </Select>
            </FormControl>

            {/* #2: optional multi-line item description (unicode supported). */}
            <TextField
              label='Description (optional)'
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              multiline
              minRows={2}
              size='small'
              placeholder='e.g. Fresh paneer cooked in rich butter tomato gravy.'
            />

            <FormControl fullWidth size='small'>
              <InputLabel>Department / Printer</InputLabel>
              <Select
                value={selectedDepartment}
                label='Department / Printer'
                onChange={(e) => setSelectedDepartment(e.target.value)}>
                {departments.map((dept) => (
                  <MenuItem key={dept.id ?? dept.Id} value={dept.id ?? dept.Id}>
                    {dept.department}
                    {dept.Printername || dept.printername
                      ? ` — ${dept.Printername || dept.printername}`
                      : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              select
              label='Select Category'
              fullWidth
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              required>
              {categories.map((cat) => (
                <MenuItem key={cat.id} value={cat.id}>
                  {cat.category_name}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label='Item Name'
              fullWidth
              value={itemName}
              onChange={(e) => setItemName(e.target.value)}
              required
            />

            {/* #2: Item image — any size pick is auto center-cropped, stored at 400px. */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  width: 110,
                  height: 110,
                  borderRadius: 1.5,
                  border: '1px dashed #cbd5e1',
                  bgcolor: '#f8fafc',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  flexShrink: 0,
                }}>
                {itemImage ? (
                  <img
                    src={itemImage}
                    alt='item'
                    style={{ width: 110, height: 110, objectFit: 'cover' }}
                  />
                ) : (
                  <Typography sx={{ fontSize: '0.6rem', color: '#94a3b8' }}>
                    Image
                  </Typography>
                )}
              </Box>
              <Button variant='outlined' component='label' size='small'>
                {itemImage ? 'Change Image' : 'Upload Image'}
                <input
                  type='file'
                  accept='image/*'
                  hidden
                  onChange={async (e) => {
                    const f = e.target.files && e.target.files[0];
                    if (f) setItemImage(await cropTo70(f));
                    e.target.value = '';
                  }}
                />
              </Button>
              {itemImage && (
                <Button
                  variant='text'
                  color='error'
                  size='small'
                  onClick={() => setItemImage('')}>
                  Remove
                </Button>
              )}
            </Box>

            <TextField
              label='HSN Code'
              fullWidth
              value={hsnCode}
              onChange={(e) => setHsnCode(e.target.value)}
            />

            {isVatApplicable && (
              <TextField
                label='VAT Rate (%)'
                type='number'
                fullWidth
                value={vatRate}
                onChange={(e) => handleTaxChange(e.target.value, 'vat')}
                required
              />
            )}
            {/* ✅ FIX 2: 'gst' type parameter is now being passed */}
            {!isVatApplicable && (
              <TextField
                label='GST (%)'
                type='number'
                fullWidth
                value={gst}
                onChange={(e) => handleTaxChange(e.target.value, 'gst')}
              />
            )}

            <TextField
              label='Price (Exclude GST)'
              type='number'
              fullWidth
              value={excludePrice}
              onChange={(e) => handleExcludeChange(e.target.value)}
            />

            {/* ✅ FIX 1: handleIncludeChange is now being used */}
            <TextField
              label='Price (Include GST)'
              type='number'
              fullWidth
              value={includePrice}
              onChange={(e) => handleIncludeChange(e.target.value)}
            />

            <Button
              variant='contained'
              size='large'
              onClick={saveItem}
              disabled={savingItem}
              sx={{ mt: 2, py: 1.5 }}>
              {savingItem ? (
                <>
                  <CircularProgress size={20} color='#fff' sx={{ mr: 1 }} />
                  Saving...
                </>
              ) : (
                'Save Item'
              )}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* ==================== ITEM REPORT SECTION ==================== */}
      <Card sx={{ borderRadius: 4, boxShadow: 3 }}>
        <CardContent sx={{ pb: 1 }}>
          <Typography variant='h6' fontWeight={600} sx={{ mb: 3 }}>
            All Items
          </Typography>

          {/* Filters */}
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
              mb: 3,
              alignItems: 'center',
            }}>
            <TextField
              label='Search Item'
              size='small'
              value={searchItem}
              onChange={(e) => setSearchItem(e.target.value)}
              sx={{ minWidth: 220 }}
            />

            <TextField
              select
              label='Category'
              size='small'
              value={searchCategory}
              onChange={(e) => setSearchCategory(e.target.value)}
              sx={{ minWidth: 180 }}>
              <MenuItem value=''>All Categories</MenuItem>
              {categoriesList.map((c, i) => (
                <MenuItem key={i} value={c}>
                  {c}
                </MenuItem>
              ))}
            </TextField>

            <Box sx={{ ml: 'auto', display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant='outlined'
                color='info'
                startIcon={<FileDownloadIcon />}
                onClick={downloadItemTemplate}>
                Download Template
              </Button>
              <Button
                variant='contained'
                color='success'
                component='label'
                startIcon={<FileUploadIcon />}
                disabled={importing}>
                {importing ? 'Importing...' : 'Import Excel'}
                <input
                  type='file'
                  hidden
                  accept='.xlsx, .xls, .csv'
                  onChange={handleImportExcel}
                />
              </Button>
              <Button
                variant='outlined'
                startIcon={<FileDownloadIcon />}
                onClick={exportToExcel}>
                Export Excel
              </Button>
            </Box>
          </Box>

          {/* Table */}
          <TableContainer>
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                  <TableCell>#</TableCell>
                  <TableCell
                    onClick={() => handleSort('name')}
                    sx={{ cursor: 'pointer', fontWeight: 600 }}>
                    Item Name
                  </TableCell>
                  <TableCell
                    onClick={() => handleSort('categoryName')}
                    sx={{ cursor: 'pointer', fontWeight: 600 }}>
                    Category
                  </TableCell>
                  <TableCell
                    onClick={() => handleSort('price')}
                    sx={{ cursor: 'pointer', fontWeight: 600 }}>
                    Price
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Sellable</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paged.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align='center' sx={{ py: 6 }}>
                      <Typography color='text.secondary'>
                        No items found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paged.map((item, index) => {
                    // isSellable defaults true on the entity, so legacy rows
                    // that pre-date the column read back as true. Treat any
                    // undefined value as true here too for the same reason.
                    const sellable = item.isSellable !== false;
                    return (
                    <TableRow key={item.id} hover>
                      <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell>{item.name}</TableCell>
                      <TableCell>{item.categoryName}</TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>
                        {Companycurrency} {item.price}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={item.isActive ? 'Active' : 'Inactive'}
                          color={item.isActive ? 'success' : 'error'}
                          size='small'
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={sellable ? 'Sellable' : 'Ingredient'}
                          color={sellable ? 'primary' : 'default'}
                          size='small'
                        />
                      </TableCell>
                      <TableCell>
                        <Button
                          size='small'
                          variant='contained'
                          color={item.isActive ? 'warning' : 'success'}
                          onClick={() => toggleStatus(item.id)}
                          sx={{ mr: 1 }}>
                          {item.isActive ? 'Deactivate' : 'Activate'}
                        </Button>
                        <Button
                          size='small'
                          variant='outlined'
                          color={sellable ? 'inherit' : 'primary'}
                          onClick={() => toggleSellable(item.id, sellable)}
                          sx={{ mr: 1 }}>
                          {sellable ? 'Mark Ingredient' : 'Mark Sellable'}
                        </Button>
                        <Button
                          size='small'
                          variant='contained'
                          onClick={() => openEdit(item)}>
                          Update
                        </Button>
                      </TableCell>
                    </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component='div'
            count={filtered.length}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 15, 25]}
          />
        </CardContent>
      </Card>

      {/* ==================== EDIT DIALOG ==================== */}
      <Dialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        maxWidth='sm'
        fullWidth>
        <DialogTitle>Update Item</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            label='Item Name'
            fullWidth
            margin='dense'
            value={editItem?.name || ''}
            onChange={(e) => setEditItem({ ...editItem, name: e.target.value })}
          />
          <TextField
            label='Price'
            type='number'
            fullWidth
            margin='dense'
            value={editItem?.price || ''}
            onChange={(e) =>
              setEditItem({ ...editItem, price: e.target.value })
            }
          />
          {/* #2: edit the item image (auto center-cropped, stored at 400px). */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1.5 }}>
            <Box
              sx={{
                width: 110,
                height: 110,
                borderRadius: 1.5,
                border: '1px dashed #cbd5e1',
                bgcolor: '#f8fafc',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                flexShrink: 0,
              }}>
              {editItem?.image ? (
                <img
                  src={editItem.image}
                  alt='item'
                  style={{ width: 110, height: 110, objectFit: 'cover' }}
                />
              ) : (
                <Typography sx={{ fontSize: '0.6rem', color: '#94a3b8' }}>
                  Image
                </Typography>
              )}
            </Box>
            <Button variant='outlined' component='label' size='small'>
              {editItem?.image ? 'Change Image' : 'Upload Image'}
              <input
                type='file'
                accept='image/*'
                hidden
                onChange={async (e) => {
                  const f = e.target.files && e.target.files[0];
                  if (f) setEditItem({ ...editItem, image: await cropTo70(f) });
                  e.target.value = '';
                }}
              />
            </Button>
            {editItem?.image && (
              <Button
                variant='text'
                color='error'
                size='small'
                onClick={() => setEditItem({ ...editItem, image: '' })}>
                Remove
              </Button>
            )}
          </Box>
          <TextField
            margin='dense'
            label='Description'
            fullWidth
            multiline
            minRows={2}
            variant='outlined'
            value={editItem?.description || ''}
            onChange={(e) => setEditItem({ ...editItem, description: e.target.value })}
            sx={{ mt: 1.5 }}
          />
          <FormControl fullWidth size='small' sx={{ mt: 1.5 }}>
            <InputLabel>Food Type</InputLabel>
            <Select
              value={editItem?.foodType || 'Veg'}
              label='Food Type'
              onChange={(e) => setEditItem({ ...editItem, foodType: e.target.value })}>
              <MenuItem value='Veg'>Veg</MenuItem>
              <MenuItem value='NonVeg'>Non-Veg</MenuItem>
              <MenuItem value='Egg'>Egg</MenuItem>
              <MenuItem value='Vegan'>Vegan</MenuItem>
              <MenuItem value='Jain'>Jain</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button variant='contained' onClick={updateItem}>
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* ==================== IMPORT PROGRESS / REPORT DIALOG ==================== */}
      <Dialog
        open={
          importing ||
          importReport.success.length +
            importReport.skipped.length +
            importReport.failed.length >
            0
        }
        onClose={() => {
          if (importing) return;
          setImportReport({ success: [], skipped: [], failed: [] });
        }}
        maxWidth='sm'
        fullWidth>
        <DialogTitle sx={{ bgcolor: '#1976d2', color: '#fff', fontWeight: 700 }}>
          {importing ? 'Importing Items...' : 'Import Report'}
        </DialogTitle>
        <DialogContent dividers>
          {importing ? (
            <Box sx={{ py: 3, textAlign: 'center' }}>
              <CircularProgress sx={{ mb: 2 }} />
              <Typography>
                Processing {importProgress.done} of {importProgress.total}...
              </Typography>
              <Typography variant='caption' color='text.secondary'>
                Auto-creating missing categories as needed.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                  label={`Added: ${importReport.success.length}`}
                  color='success'
                />
                <Chip
                  label={`Skipped: ${importReport.skipped.length}`}
                  color='warning'
                />
                <Chip
                  label={`Failed: ${importReport.failed.length}`}
                  color='error'
                />
              </Box>
              {importReport.failed.length > 0 && (
                <Box>
                  <Typography variant='subtitle2' color='error' gutterBottom>
                    Failed rows
                  </Typography>
                  {importReport.failed.map((r, i) => (
                    <Typography
                      key={`f-${i}`}
                      variant='caption'
                      display='block'
                      sx={{ pl: 1 }}>
                      • Row {r.row}: {r.name} — {r.reason}
                    </Typography>
                  ))}
                </Box>
              )}
              {importReport.skipped.length > 0 && (
                <Box>
                  <Typography
                    variant='subtitle2'
                    color='warning.main'
                    gutterBottom>
                    Skipped (duplicates)
                  </Typography>
                  {importReport.skipped.map((r, i) => (
                    <Typography
                      key={`s-${i}`}
                      variant='caption'
                      display='block'
                      sx={{ pl: 1 }}>
                      • Row {r.row}: {r.name} ({r.category})
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setImportReport({ success: [], skipped: [], failed: [] })
            }
            disabled={importing}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
